import { waybillTitleStatuses, waybillTitleStatusesEnum, waybillTitleTypesEnum } from "helpers/enums";
import { enumLabel } from "helpers/system";
import React from "react";
import type { WaybillListModel, WaybillTitleStatus, WaybillTitleType } from "sources/waybills";

const driverTitleTypes: WaybillTitleType[] = [waybillTitleTypesEnum.T1, waybillTitleTypesEnum.T3];
const responseTitleTypes: WaybillTitleType[] = [
    waybillTitleTypesEnum.T2,
    waybillTitleTypesEnum.T4,
    waybillTitleTypesEnum.T8,
];
const signatureRequiredStatuses: WaybillTitleStatus[] = [
    waybillTitleStatusesEnum.ReceivedOrCreated,
    waybillTitleStatusesEnum.FailedToSend,
    waybillTitleStatusesEnum.InvalidSignature,
];
const signatureRequiredTitleTypes = [...driverTitleTypes, ...responseTitleTypes];

/** Use the received state for driver confirmation and preserve response-title signing/retry states. */
const getSignatureRequiredStatuses = (titleType: WaybillTitleType): WaybillTitleStatus[] =>
    driverTitleTypes.includes(titleType) ? [waybillTitleStatusesEnum.ReceivedOrCreated] : signatureRequiredStatuses;

export const waybillRegistryStatuses = [
    {
        label: "Т1 требует подписания",
        value: "T1SignRequired",
        titleType: waybillTitleTypesEnum.T1,
        requiresSignature: true,
    },
    { label: "Т1 получен", value: "T1Received", titleType: waybillTitleTypesEnum.T1, requiresSignature: false },
    {
        label: "Т2 требует подписания",
        value: "T2SignRequired",
        titleType: waybillTitleTypesEnum.T2,
        requiresSignature: true,
    },
    { label: "Т2 подписан", value: "T2Signed", titleType: waybillTitleTypesEnum.T2, requiresSignature: false },
    {
        label: "Т3 требует подписания",
        value: "T3SignRequired",
        titleType: waybillTitleTypesEnum.T3,
        requiresSignature: true,
    },
    { label: "Т3 получен", value: "T3Received", titleType: waybillTitleTypesEnum.T3, requiresSignature: false },
    {
        label: "Т4 требует подписания",
        value: "T4SignRequired",
        titleType: waybillTitleTypesEnum.T4,
        requiresSignature: true,
    },
    { label: "Т4 подписан", value: "T4Signed", titleType: waybillTitleTypesEnum.T4, requiresSignature: false },
    {
        label: "Требуется сформировать Т8",
        value: "T8ChangeRequired",
        titleType: waybillTitleTypesEnum.T8,
        requiresSignature: false,
    },
    {
        label: "Т8 требует подписания",
        value: "T8SignRequired",
        titleType: waybillTitleTypesEnum.T8,
        requiresSignature: true,
    },
    { label: "Т8 подписан", value: "T8Signed", titleType: waybillTitleTypesEnum.T8, requiresSignature: false },
    {
        label: enumLabel(waybillTitleStatuses, waybillTitleStatusesEnum.InvalidSignature),
        value: waybillTitleStatusesEnum.InvalidSignature,
        titleType: null,
        requiresSignature: true,
    },
] as const;

export type WaybillRegistryStatus = (typeof waybillRegistryStatuses)[number]["value"];

export const waybillRequiresSignature = ({ lastTitleType, lastTitleStatus }: WaybillListModel) =>
    !!lastTitleType &&
    signatureRequiredTitleTypes.includes(lastTitleType) &&
    !!lastTitleStatus &&
    getSignatureRequiredStatuses(lastTitleType).includes(lastTitleStatus);

export const getWaybillRegistryStatus = (waybill: WaybillListModel): WaybillRegistryStatus | null => {
    const { lastTitleType } = waybill;
    if (
        lastTitleType &&
        responseTitleTypes.includes(lastTitleType) &&
        waybill.lastTitleStatus === waybillTitleStatusesEnum.InvalidSignature
    ) {
        return waybillTitleStatusesEnum.InvalidSignature;
    }
    if (lastTitleType === waybillTitleTypesEnum.T8) {
        return waybillRequiresSignature(waybill) ? "T8SignRequired" : "T8Signed";
    }
    if (waybill.changeTitleRequired) {
        return "T8ChangeRequired";
    }
    if (lastTitleType === waybillTitleTypesEnum.T1) {
        return waybillRequiresSignature(waybill) ? "T1SignRequired" : "T1Received";
    }
    if (lastTitleType === waybillTitleTypesEnum.T3) {
        return waybillRequiresSignature(waybill) ? "T3SignRequired" : "T3Received";
    }
    if (lastTitleType === waybillTitleTypesEnum.T2) {
        return waybillRequiresSignature(waybill) ? "T2SignRequired" : "T2Signed";
    }
    if (lastTitleType === waybillTitleTypesEnum.T4) {
        return waybillRequiresSignature(waybill) ? "T4SignRequired" : "T4Signed";
    }
    return null;
};

export const getWaybillRegistryStatusLabel = (waybill: WaybillListModel) => {
    const status = getWaybillRegistryStatus(waybill);
    if (status === waybillTitleStatusesEnum.InvalidSignature) {
        return `${waybill.lastTitleType?.replace("T", "Т")} — ${enumLabel(waybillTitleStatuses, status).toLowerCase()}`;
    }
    return waybillRegistryStatuses.find(({ value }) => value === status)?.label || "Статус не определён";
};

export interface WaybillTitleStatusInfo {
    badgeText: string;
    isRed: boolean;
    isGreen: boolean;
    statusText: string;
}

export const getWaybillTitleStatusInfo = (waybill: {
    lastTitleType?: WaybillTitleType | string | null;
    lastTitleStatus?: WaybillTitleStatus | string | null;
    lastTitleSignatureFileId?: number | null;
}): WaybillTitleStatusInfo | null => {
    const { lastTitleType, lastTitleStatus, lastTitleSignatureFileId } = waybill;
    if (!lastTitleType) {
        return null;
    }

    const badgeText = lastTitleType.replace("T", "Т");
    if (
        lastTitleStatus &&
        [
            waybillTitleStatusesEnum.InvalidSignature,
            waybillTitleStatusesEnum.Failed,
            waybillTitleStatusesEnum.FailedToSend,
        ].includes(lastTitleStatus)
    ) {
        return {
            badgeText,
            isRed: true,
            isGreen: false,
            statusText: `${badgeText} — ${enumLabel(waybillTitleStatuses, lastTitleStatus).toLowerCase()}`,
        };
    }
    const isSignatureRequired =
        !lastTitleSignatureFileId ||
        (!!lastTitleStatus && signatureRequiredStatuses.includes(lastTitleStatus as WaybillTitleStatus));

    switch (lastTitleType) {
        case waybillTitleTypesEnum.T1:
            return {
                badgeText,
                isRed: true,
                isGreen: false,
                statusText: lastTitleSignatureFileId ? "Требуется создать Т2" : "Требуется подписание водителем Т1",
            };
        case waybillTitleTypesEnum.T2:
            return {
                badgeText,
                isRed: isSignatureRequired,
                isGreen: !isSignatureRequired,
                statusText: isSignatureRequired ? "Требуется подписать Т2" : "Т2 подписан",
            };
        case waybillTitleTypesEnum.T3:
            return {
                badgeText,
                isRed: true,
                isGreen: false,
                statusText: lastTitleSignatureFileId ? "Требуется создать Т4" : "Требуется подписание водителем Т3",
            };
        case waybillTitleTypesEnum.T4:
            return {
                badgeText,
                isRed: isSignatureRequired,
                isGreen: !isSignatureRequired,
                statusText: isSignatureRequired ? "Требуется подписать Т4" : "Т4 подписан",
            };
        case waybillTitleTypesEnum.T8:
            return {
                badgeText,
                isRed: isSignatureRequired,
                isGreen: !isSignatureRequired,
                statusText: isSignatureRequired ? "Требуется подписать Т8" : "Т8 подписан",
            };
        default:
            return {
                badgeText,
                isRed: isSignatureRequired,
                isGreen: !isSignatureRequired,
                statusText: isSignatureRequired ? `Требуется подписать ${badgeText}` : `${badgeText} подписан`,
            };
    }
};

export type SignatureFilterValue = "all" | "true" | "false";

export interface WaybillStatusFilterValue {
    status?: WaybillRegistryStatus | "all" | string | string[];
    signatureRequired?: SignatureFilterValue | string;
}

interface WaybillStatusFilterProps {
    filtered: Record<string, { value?: WaybillStatusFilterValue }>;
    column: { id: string };
    onFilteredChange: (payload: {
        id: string;
        value: WaybillStatusFilterValue;
        filterType: "prepared";
        preparedFilter: {};
    }) => void;
}

const titleStatusFilter = (requiresSignature: boolean, titleType: WaybillTitleType) =>
    requiresSignature
        ? { lastTitleStatus: { in: getSignatureRequiredStatuses(titleType) } }
        : { not: [{ lastTitleStatus: { in: getSignatureRequiredStatuses(titleType) } }] };

const buildSingleStatusFilter = (statusValue: string, signatureValue: boolean | null) => {
    const statusOption = waybillRegistryStatuses.find(({ value }) => value === statusValue);
    if (!statusOption) {
        return null;
    }
    if (signatureValue !== null && signatureValue !== statusOption.requiresSignature) {
        return { id: -1 };
    }
    if (statusOption.value === waybillTitleStatusesEnum.InvalidSignature) {
        return { lastTitleStatus: waybillTitleStatusesEnum.InvalidSignature };
    }
    if (!statusOption.titleType) {
        return {};
    }
    const statusFilter =
        statusOption.titleType && signatureRequiredTitleTypes.includes(statusOption.titleType)
            ? titleStatusFilter(statusOption.requiresSignature, statusOption.titleType)
            : {};
    return { lastTitleType: statusOption.titleType, ...statusFilter };
};

export const buildStatusFilter = ({ status, signatureRequired }: WaybillStatusFilterValue) => {
    const signatureValue = signatureRequired === "all" || !signatureRequired ? null : signatureRequired === "true";

    const statusList = Array.isArray(status)
        ? status.filter((s) => s && s !== "all")
        : status && status !== "all"
          ? [status]
          : [];

    if (statusList.length > 0) {
        const filters = statusList.map((s) => buildSingleStatusFilter(s, signatureValue)).filter(Boolean);
        if (filters.length === 0) {
            return {};
        }
        if (filters.length === 1) {
            return filters[0]!;
        }
        return { or: filters };
    }

    if (signatureValue !== null) {
        const signatureFilter = {
            or: [
                {
                    lastTitleType: { in: driverTitleTypes },
                    lastTitleStatus: waybillTitleStatusesEnum.ReceivedOrCreated,
                },
                {
                    lastTitleType: { in: responseTitleTypes },
                    lastTitleStatus: { in: signatureRequiredStatuses },
                },
            ],
        };
        return signatureValue ? signatureFilter : { not: [signatureFilter] };
    }
    return {};
};

export const WaybillStatusFilter = ({ filtered, column, onFilteredChange }: WaybillStatusFilterProps) => {
    const value = filtered[column.id]?.value || { status: "all", signatureRequired: "all" };
    const update = (nextValue: WaybillStatusFilterValue) =>
        onFilteredChange({
            id: column.id,
            value: nextValue,
            filterType: "prepared",
            preparedFilter: buildStatusFilter(nextValue),
        });

    return (
        <div>
            <select
                className="r-item__filter-select r-item__filter-select--wide field-wrap__input mb-1"
                name="waybillStatus"
                aria-label="Статус ЭТрН"
                value={value.status}
                onChange={({ target }) =>
                    update({ ...value, status: target.value as WaybillStatusFilterValue["status"] })
                }
            >
                <option value="all">Все статусы</option>
                {waybillRegistryStatuses.map(({ label, value: optionValue }) => (
                    <option key={optionValue} value={optionValue}>
                        {label}
                    </option>
                ))}
            </select>
            <select
                className="r-item__filter-select r-item__filter-select--wide field-wrap__input"
                name="waybillSignatureRequired"
                aria-label="Потребность в подписи"
                value={value.signatureRequired}
                onChange={({ target }) => update({ ...value, signatureRequired: target.value as SignatureFilterValue })}
            >
                <option value="all">Любая потребность в подписи</option>
                <option value="true">Требует подписания</option>
                <option value="false">Не требует подписания</option>
            </select>
        </div>
    );
};
